import React from 'react';
import { FileUploadCard, FileUploadCardProps as FileUploadCardProps_ } from '@jkhy/vsg-design-system';
import RequiredDocument_, {
  Attachment as Attachment_,
  AttachmenResponse as AttachmenResponse_
} from '../../../data/models/RequiredDocumet';
import DocAPI from '../../../data/doc-api';
import { AntivirusResponseStatus } from '../../../data/models/Enum';
import { PasswordProtectedStatus } from '../../../utils/enum';
import { camelCaseSpacing } from '../../../utils/helper';
import { invalidCharactersRegex } from '../../../utils/regex';

const DOCUMENT_NAME_MAX_LENGTH = 50;

export type FileState = {
    dragging: boolean
    files: Attachment_[]
    isLoading: boolean
    submitError: string
    success: boolean
}

type FileUploadCardWrapperProps = FileUploadCardProps_ & {
    keyValue: string;
    document: RequiredDocument_,
    getRef: (docId: number) => any
    // Events callbacks
    onSubmitFileCallback?: () => Promise<void>
    onFileUploadCallback?: (docGuid:string, attachments: Attachment_[]) => void;
    onFileRemoveCallback?: (docGuid:string, attachments: Attachment_) => void;
};

const FileUploadCardWrapper = (props: FileUploadCardWrapperProps) => {
  const {
    keyValue,
    dataUI,
    title,
    className,
    isMultiple,
    removeOldErrorFiles,
    cardErrorMessage,
    details,
    document,
    type,
    additionalInfoText = '',
    onRef,
    getRef,
    onSubmitFileCallback,
    onFileUploadCallback,
    onFileRemoveCallback,
  } = props;

  const getErrorTitle = (error) => {
    switch (error) {
      case AntivirusResponseStatus.NoThreatFound:
      case AntivirusResponseStatus.ThreatFound:
        return camelCaseSpacing(error);
      case PasswordProtectedStatus:
        return 'Password protected document';
      default: return error;
    }
  };

  const onUploadFile = async (newFile: Attachment_, fileRef:any):Promise<Attachment_> => {
    const { file, } = newFile;

    fileRef.updateLoading(newFile, true);

    // call server
    const bodyFormData = new FormData();
    bodyFormData.append('docGuid', document.guid);
    bodyFormData.append('file', file);

    return DocAPI.uploadFile(bodyFormData).then(res => {
      if (res.errorCode > 0) {
        const invalidFile = {
          ...newFile,
          error: `${res.result}`,
          errorDescription: res.message,
        };

        fileRef.updateLoading(invalidFile, false);
        fileRef.onFileError(invalidFile, getErrorTitle(invalidFile.error), res.message);

        return invalidFile;
      }

      fileRef.updateLoading(newFile, false);

      const castResult = res?.result as AttachmenResponse_;
      const { attachmentGuid, } = castResult;
      fileRef.onFileSuccess(newFile, attachmentGuid);
      return { ...newFile, guid: attachmentGuid, };
    });
  };

  const onSubmit = () => {
    const fileRef = getRef(document.id);
    const fileState: FileState = fileRef?.state;

    const hasFiles: Attachment_[] = fileState?.files;
    if (!hasFiles) {
      return false;
    }

    return DocAPI.submit(document.guid).then(async (res) => {
      if (res.errorCode > 0 || res.error) {
        fileRef.onSubmitError(res.message || res?.error?.message);
        return false;
      }

      if (typeof onSubmitFileCallback === 'function') {
        await onSubmitFileCallback();
      }
      fileRef.onSubmitSuccess('Your document was submitted.');

      return true;
    });
  };

  const onChangeFile = async (newFiles: Attachment_[]) => {
    const fileRef = getRef(document.id);

    const { submitError, } = fileRef?.state;
    if (submitError) {
      // Clear old submit error message
      fileRef.onSubmitError('');
    }

    if (newFiles.length > 0) {
      const promises = [];
      newFiles.forEach(f => {
        if (!f.error) { // Could be passsword protected pdf error; No need to call server
          const promise = onUploadFile(f, fileRef);
          promises.push(promise);
        } else {
          if (f.error === 'invalid name') {
            if (invalidCharactersRegex.test(f.file.name)) {
              fileRef.onFileError(f, 'File name cannot contain any of the following characters \\ / : * ? " < > | &');
            }
          }

          promises.push(f);
        }
      });

      Promise.all(promises).then(results => {
        if (typeof onFileUploadCallback === 'function') onFileUploadCallback(document.guid, results);
      });
    }
  };

  const onRemoveFile = async (removedFile:Attachment_, removeWithAnimation: any): Promise<boolean> => {
    if (!removedFile?.guid) {
      if (typeof onFileRemoveCallback === 'function') onFileRemoveCallback(document.guid, removedFile);
      removeWithAnimation();
      return false;
    }

    const fileRef = getRef(document.id);
    fileRef.updateLoading(removedFile, true);

    const result = await DocAPI.removeFile(removedFile.guid, document.guid);
    const hasError = (result.errorCode > 0 || result?.error);
    if (hasError) {
      const errorMsg = result?.error?.message ?? result?.message;
      fileRef.onFileError({ ...removedFile, error: errorMsg, }, errorMsg);
      fileRef.updateLoading(removedFile, false);
    } else {
      fileRef.updateLoading(removedFile, false);
      if (typeof onFileRemoveCallback === 'function') onFileRemoveCallback(document.guid, removedFile);
      removeWithAnimation();
    }

    return !hasError;
  };

  const attachedFiles = document?.attachments?.filter(a => !!a.documentName || !!a?.file?.name).map(attachment => {
    const {
      guid, documentName, file, error, errorDescription,
    } = attachment;

    const name = documentName || file?.name;
    const docName = name.length > DOCUMENT_NAME_MAX_LENGTH ? `${name.substring(0, DOCUMENT_NAME_MAX_LENGTH)}...` : name;
    return {
      guid,
      file: new File([], docName),
      uploadSuccessful: !error,
      error: getErrorTitle(error),
      errorDescription,
    };
  });

  return (
    <FileUploadCard
      key={keyValue}
      className={className}
      files={attachedFiles}
      isMultiple={isMultiple ?? false}
      removeOldErrorFiles={removeOldErrorFiles ?? false}
      cardErrorMessage={cardErrorMessage ?? ''}
      title={title}
      maxFileSizeMB={parseInt(window.CONFIG?.App?.DocumentMaxFileSizeMB, 10)}
      additionalInfoText={additionalInfoText}
      details={details ?? []}
      onChange={onChangeFile}
      onSubmit={onSubmit}
      onDelete={onRemoveFile}
      onRef={onRef}
      dataUI={dataUI}
      type={type ?? 'default'}
    />
  );
};

export default FileUploadCardWrapper;
